import { Button, PetitionCard, Spinner, Text } from '@change/ds-core-ui';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core/react/i18n';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';
import { capitalizeTopic } from 'src/app/shared/utils/topic';

import { useVictoriousPetitionsSection } from './hooks/useVictoriousPetitionsSection';

export const VictoriousPetitionsSection: React.FC = () => {
	const { translatePlural, getCountry, translate } = useI18n();
	const {
		data: { displayed, name, petitions, hasNextPage, nextPageLoading },
		actions: { loadMore },
	} = useVictoriousPetitionsSection();

	const { containerRef, loadingRef, loadMoreRef, recordListEvent, lastEvent, getListItemRef } =
		useLazyLoadedListFocus();

	if (!displayed) {
		return null;
	}

	return (
		<div className="flex flex-col gap-6" data-qa="victories-container">
			<Text as="h2" variant="heading" size="small" className="text-typography-strong mb-6" data-qa="victories-heading">
				<Translate
					value="corgi.topic_page.victorious_petitions.heading"
					replacements={{ name: capitalizeTopic(name) }}
				/>
			</Text>

			<div ref={containerRef} className="flex flex-col items-center gap-6">
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{petitions.map((petition, index) => (
						<div className="flex" key={petition.id}>
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<div className="sr-only" role="alert">
									<TranslatePlural
										value="corgi.topic_page.petition_section.fetched_more"
										count={petitions.length - lastEvent.previousItemsCount}
										replacements={{ count: petitions.length - lastEvent.previousItemsCount }}
									/>
								</div>
							)}

							<PetitionCard
								ref={getListItemRef(index)}
								imageUrl={petition.photo?.petitionMedium.url}
								data-qa="victories-petition-card"
								isVictory
								victoryLabel={translate('corgi.components.petition_card.victory')}
								victoryLabelClassName="capitalize bg-fill-accent-primary-strong"
								signaturesLabel={translatePlural(
									'corgi.components.petition_card.signatures',
									petition.signatureState.signatureCount.displayed,
									{
										countLocalized: petition.signatureState.signatureCount.displayedLocalized,
									},
								)}
								location={
									petition.relevantLocationLocalizedName || getCountry(petition.country.countryCode)?.name || ''
								}
								className="w-full"
								petitionId={petition.id}
								petitionLinkUrl={`/p/${petition.slug}?source_location=topics_page`}
								title={petition.ask}
							/>
						</div>
					))}
				</div>

				{nextPageLoading && (
					<>
						<div className="sr-only" role="alert">
							<Translate value="corgi.topic_page.victorious_petitions.fetching_more" />
						</div>
						<Spinner className="mx-auto mt-6" ref={loadingRef} />
					</>
				)}

				{!nextPageLoading && hasNextPage && (
					<Button
						data-qa="victories-show-more-button"
						size="small"
						className="max-w-[350px] max-md:w-full!"
						variant="secondary"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: petitions.length });
							loadMore();
						}}
						ref={loadMoreRef}
					>
						<Translate value="corgi.topic_page.victorious_petitions.show_more" />
					</Button>
				)}
			</div>
		</div>
	);
};
